<template>
  <div class="p-8 max-w-[1200px] mx-auto space-y-8 min-h-screen">
    <!-- Header -->
    <div class="flex items-center justify-between gap-4">
      <div class="space-y-2">
        <h1 class="text-3xl font-black tracking-tight text-foreground flex items-center gap-3">
          <CalendarDays class="w-8 h-8 text-indigo-500" />
          Mes Abonnements
        </h1>
        <p class="text-muted-foreground font-medium italic max-w-2xl">
          Gérez vos abonnements récurrents et configurez des alertes pour ne plus oublier de vous désabonner.
        </p>
      </div>
      <Button variant="finance" @click="openFormModal()">
        <Plus class="w-4 h-4 mr-2" />
        Ajouter un abonnement
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center p-20 space-y-4">
      <RefreshCw class="w-10 h-10 text-muted-foreground/30 animate-spin" />
      <span class="text-muted-foreground font-bold italic text-xs uppercase">Chargement...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="subscriptions.length === 0" class="flex flex-col items-center justify-center p-16 bg-card border-2 border-dashed border-border rounded-3xl text-center space-y-4 shadow-xl">
      <div class="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center text-indigo-400">
        <CreditCard class="w-10 h-10" />
      </div>
      <div class="space-y-1">
        <h3 class="text-xl font-black text-foreground">Aucun abonnement</h3>
        <p class="text-muted-foreground max-w-sm italic">Vous n'avez pas encore configuré d'abonnement régulier.</p>
      </div>
    </div>

    <!-- Subscriptions List -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="sub in subscriptions" :key="sub.id" class="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between shadow-lg shadow-black/5 hover:shadow-black/10 hover:border-indigo-500/30 transition-all group">
        <div class="space-y-4">
          <div class="flex items-start justify-between">
            <div class="space-y-1">
              <h3 class="font-black text-lg text-foreground">{{ sub.name }}</h3>
              <Badge variant="outline" class="font-bold text-[10px] uppercase tracking-tighter" :class="sub.period === 'monthly' ? 'text-indigo-400 border-indigo-500/20 bg-indigo-500/10' : 'text-emerald-400 border-emerald-500/20 bg-emerald-500/10'">
                {{ sub.period === 'monthly' ? 'Mensuel' : 'Annuel' }}
              </Badge>
            </div>
            <div class="text-right">
              <div class="font-black text-xl text-red-400 tabular-nums">-{{ formatAmount(sub.price) }}€</div>
            </div>
          </div>
          
          <div class="space-y-2 text-sm">
            <div v-if="sub.comment" class="bg-muted/30 p-2 rounded-lg text-xs text-muted-foreground italic line-clamp-2 mb-3">
              {{ sub.comment }}
            </div>
            <div class="flex items-center justify-between">
              <span class="text-muted-foreground font-medium flex items-center gap-2"><Calendar class="w-4 h-4" /> Prochain renouvellement</span>
              <span class="font-bold text-foreground tabular-nums">{{ formatDateFr(sub.renewalDate) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-muted-foreground font-medium flex items-center gap-2">
                <Bell class="w-4 h-4" :class="sub.alertMode !== 'none' ? 'text-amber-500' : 'text-muted-foreground/50'" /> 
                Date d'alerte
              </span>
              <span class="font-bold tabular-nums" :class="sub.alertMode !== 'none' ? 'text-amber-500' : 'text-muted-foreground/50'">
                {{ sub.alertMode !== 'none' && sub.alertDate ? formatDateFr(sub.alertDate) : 'Désactivée' }}
              </span>
            </div>
          </div>
        </div>
        
        <div class="pt-6 mt-6 border-t border-border flex items-center justify-end gap-2">
          <Button variant="ghost" size="icon" @click="openFormModal(sub)" class="h-8 w-8 hover:bg-muted" aria-label="Modifier">
            <Pencil class="w-4 h-4 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon" @click="promptDelete(sub.id)" class="h-8 w-8 hover:bg-red-500/20 hover:text-red-400" aria-label="Supprimer">
            <Trash2 class="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>

    <SubscriptionFormModal 
      v-if="showFormModal"
      :show="showFormModal"
      :subscription="selectedSubscription"
      @close="showFormModal = false"
      @saved="onSaved"
    />

    <!-- Confirm Delete Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div class="bg-card w-full max-w-sm rounded-2xl shadow-2xl shadow-red-500/10 border border-red-500/20 overflow-hidden">
        <div class="p-6 space-y-4">
          <div class="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 mx-auto">
            <Trash2 class="w-6 h-6" />
          </div>
          <div class="text-center space-y-2">
            <h3 class="text-lg font-black text-foreground">Supprimer l'abonnement ?</h3>
            <p class="text-sm text-muted-foreground">Cette action est définitive et annulera les prochaines alertes.</p>
          </div>
          <div class="pt-4 flex items-center gap-3 w-full">
            <Button variant="outline" class="flex-1" @click="closeConfirmModal" :disabled="deleting">Annuler</Button>
            <Button variant="finance" class="flex-1 bg-red-500 hover:bg-red-600 border-none text-white shadow-lg shadow-red-500/20" @click="confirmDelete" :disabled="deleting">
              <RefreshCw v-if="deleting" class="w-4 h-4 mr-2 animate-spin" />
              Supprimer
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { CalendarDays, Plus, RefreshCw, CreditCard, Calendar, Bell, Pencil, Trash2 } from 'lucide-vue-next'
import { formatAmount, formatDateFr } from '@/lib/utils'
import { useGlobalState } from '@/composables/useGlobalState'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import SubscriptionFormModal from './SubscriptionFormModal.vue'

const { getAuthHeader } = useGlobalState()

const loading = ref(true)
const subscriptions = ref([])
const showFormModal = ref(false)
const selectedSubscription = ref(null)

const fetchSubscriptions = async () => {
  loading.value = true
  try {
    const res = await fetch('/api/subscriptions', { headers: getAuthHeader() })
    if (res.ok) {
      subscriptions.value = await res.json()
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const openFormModal = (sub = null) => {
  selectedSubscription.value = sub ? { ...sub } : null
  showFormModal.value = true
}

const onSaved = () => {
  showFormModal.value = false
  fetchSubscriptions()
}

const showConfirmModal = ref(false)
const confirmDeleteId = ref(null)
const deleting = ref(false)

const promptDelete = (id) => {
  confirmDeleteId.value = id
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
  confirmDeleteId.value = null
}

const confirmDelete = async () => {
  if (!confirmDeleteId.value) return
  deleting.value = true
  
  try {
    const res = await fetch(`/api/subscriptions/${confirmDeleteId.value}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    })
    if (res.ok) {
      fetchSubscriptions()
      closeConfirmModal()
    }
  } catch (e) {
    console.error(e)
  } finally {
    deleting.value = false
  }
}

onMounted(() => {
  fetchSubscriptions()
})
</script>
